/**
 * PARTNER PAGE — `htstrader.com.br/p/<slug>` (2026-09-17; visual novo em 01/10)
 * --------------------------------------------------------------------
 * Página de venda de um parceiro B2B (ex.: escritório da XP). Mesmo produto e mesmo checkout da
 * pré-venda (`CheckoutForm`), com a oferta do ACORDO do parceiro: preço, nome e preço cheio vêm de
 * `GET /api/public/partner-offer/:slug`, nunca de texto fixo — é o mesmo acordo que o servidor
 * cobra, então a página não tem como prometer um valor e o cartão receber outro.
 *
 * Sem contagem regressiva e sem contador de vagas. A ativação é em ondas, EXCETO quando o acordo tem
 * período de teste (`trialDays`), que libera o acesso na hora e não cobra nada hoje.
 *
 * Dono do acordo e da condição: o parceiro. Por isso o texto fala em "enquanto mantiver a condição
 * da parceria" sem dizer qual é — a condição é combinada entre o parceiro e o cliente dele.
 *
 * 18/10/2026 (meta de lotes, dono): acordo com `metaLotes` não é teste, é condição especial — a pessoa entra
 * sem pagar (sempre, inclusive quem já teve conta ou troca de escritório) e, em todo ciclo em que executar a meta
 * de lotes pelo HTS, ganha o benefício do acordo: isenção da mensalidade ou desconto de N%. Cancelando, o ciclo em
 * curso é acertado na hora (abaixo da meta, cobra o preço). A regra
 * exata vem pronta do servidor (`condicaoBlocos` para mostrar e `condicao` achatado, de `texto-da-condicao.ts`): a página mostra os blocos e o checkout
 * grava o MESMO texto no aceite da cobrança recorrente. Redigir a regra aqui seria uma segunda promessa,
 * que um dia diverge da que o job aplica. Os dias do 1º ciclo também vêm do servidor (30 com meta).
 *
 * 01/10/2026: a página deixa o visual escuro da pré-venda e usa o da home oficial (fundo claro, magenta,
 * cantos vivos): a mesma barra do topo, as mesmas seções da plataforma, o mesmo FAQ e o mesmo rodapé. Muda
 * só o topo (a condição do parceiro) e o formulário de assinatura, que fica numa seção escura.
 */
import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { CustomCursor, Reveal, Footer } from "@/components/site/shared";
import { getPartnerOffer, type PartnerOffer } from "@/lib/api";
import { CheckoutForm } from "@/components/site/founding";
import { reaisComCentavos } from "@/lib/dinheiro";
import { CondicaoEmBlocos } from "@/components/site/condicao-em-blocos";
import { ID_DAS_CONDICOES } from "@/lib/autorizacao";
import { Faq, Rotulo, SecoesDaPlataforma, Topbar } from "@/pages/home-oficial-page";
import "@/styles/landing-v2.css";
import "@/styles/hts-extra.css";
import "@/styles/home-oficial.css";

const ATIVACAO_PREVISTA = "18/10/2026";

// Com centavos, no formato do texto da condição (a nota de rodapé): a mesma tela não mostra dois preços (18/10).
const reais = reaisComCentavos;

const RODAPE = {
  navLinks: [{ href: "/", label: "Início" }, { href: "/recursos/", label: "Recursos" }],
  rodape: "Plataforma de análise e operação · Não é corretora",
};

export default function PartnerPage({ slug }: { slug: string }) {
  useSmoothScroll();
  const [oferta, setOferta] = useState<PartnerOffer | "encerrado" | null | undefined>(undefined);

  useEffect(() => {
    let vivo = true;
    getPartnerOffer(slug).then((r) => {
      if (!vivo) return;
      // Parceiro com campanha montada no editor tem página própria: esta aqui só encaminha, sem
      // pintar nada antes (`replace`, para o "voltar" do navegador não cair neste redirecionador).
      const nova = r && r !== "encerrado" ? r.landingUrl : null;
      if (nova) { window.location.replace(nova); return; }
      setOferta(r);
    });
    return () => { vivo = false; };
  }, [slug]);

  if (oferta === undefined) {
    return <div className="lv2 hto" style={{ minHeight: "100vh", background: "#fff" }} />;
  }

  if (oferta === "encerrado" || oferta === null) {
    const encerrado = oferta === "encerrado";
    return (
      <div className="lv2 hts-compact-display hto">
        <CustomCursor />
        <Topbar destino="/" cta="Conhecer a HTS" semNav />
        <section className="hto-ph">
          <Reveal>
            <Rotulo>{encerrado ? "Link de parceiro" : "Oferta do parceiro"}</Rotulo>
            <h1>{encerrado ? "Este link não está mais ativo." : "Não conseguimos carregar a oferta."}</h1>
            <p className="hto-ph-lead">
              {encerrado
                ? "A condição deste parceiro foi encerrada ou o endereço está incorreto. Confirme o link com quem te indicou, ou conheça a HTS pela página principal."
                : "Verifique sua conexão e recarregue a página em instantes."}
            </p>
            <a className="hto-btn hto-pri" href="/">Conhecer a HTS →</a>
          </Reveal>
        </section>
        <Footer {...RODAPE} />
      </div>
    );
  }

  const preco = reais(oferta.priceCents);
  const cheio = oferta.fullPriceCents && oferta.fullPriceCents > oferta.priceCents ? reais(oferta.fullPriceCents) : null;
  // Com período de teste, o que a pessoa precisa saber PRIMEIRO não é o preço: é que hoje ela não
  // paga. O preço vem logo atrás, porque ele existe e vai chegar.
  const teste = oferta.trialDays ?? 0;
  const meta = oferta.metaLotes && oferta.condicao ? oferta.metaLotes : null;
  const desconto = oferta.beneficio?.tipo === "desconto" ? oferta.beneficio.descontoPct : null;
  // Só contam os lotes executados pelo HTS em conta da corretora do escritório (18/10/2026): "em conta XP".
  const emConta = oferta.corretorasTexto ? ` em conta ${oferta.corretorasTexto}` : "";
  // Copy de venda (dono, 18/10: "mais marketing... HTS sem mensalidade... nota de rodapé explicando"): a promessa
  // em uma linha e a regra inteira, a mesma que o aceite grava, na nota de rodapé (*) logo abaixo do botão.
  const promessa = desconto ? `HTS com ${desconto}% off.` : "HTS sem mensalidade.";
  // Acordo COM período de teste libera na hora e não entra na fila de ondas: "ativação prevista" ali
  // seria prometer uma espera e uma cobrança que não são dele (23/09).
  const numeros: Array<[string, string]> = meta
    ? [["R$ 0", "Para entrar hoje"], [`${meta} contratos`, desconto ? `Por ciclo, e o escritório custeia ${desconto}% da mensalidade` : "Por ciclo, e o escritório custeia a mensalidade"], [preco, "Por mês, só no ciclo abaixo da meta*"]]
    : teste > 0
    ? [[preco, "Por mês, depois do teste"], [`${teste} dias`, "De teste, sem cobrança"], ["Na hora", "Acesso liberado"]]
    : [[preco, "Por mês, com a parceria"], [ATIVACAO_PREVISTA, "Ativação prevista"], ["30 dias", "1º ciclo só começa na ativação"]];

  return (
    <div className="lv2 hts-compact-display hto">
      <CustomCursor />
      <Topbar destino="#form" rotulo={oferta.partnerName} cta="Assinar" />

      <section className="hto-ph">
        <Reveal>
          <Rotulo>Condição exclusiva · {oferta.partnerName}</Rotulo>
          <h1>
            {meta ? (
              <>{promessa}<br /><span className="hto-ph-contorno">Opere {meta} contratos por ciclo{emConta}.</span></>
            ) : teste > 0 ? (
              <>{teste} dias sem pagar<br /><span className="hto-ph-contorno">depois {preco}/mês.</span></>
            ) : (
              <>{preco}/mês{cheio && <><br /><span className="hto-ph-contorno">em vez de {cheio}.</span></>}</>
            )}
          </h1>
          <p className="hto-ph-lead">
            {meta ? (
              <>
                Cliente {oferta.partnerName} entra hoje sem pagar nada. Bateu {meta} contratos no ciclo, com ordens pela HTS{emConta},{" "}
                {/* Quem custeia é o escritório (modelo do dono, 18/10): na voz da página, "nossa conta" seria a HTS. */}
                {desconto ? `o escritório custeia ${desconto}% da mensalidade.` : "o escritório custeia a mensalidade."}*
              </>
            ) : teste > 0 ? (
              <>
                Por ser cliente {oferta.partnerName}, você usa a HTS por {teste} dias sem pagar nada, com acesso
                liberado na hora. Depois são {preco}/mês enquanto mantiver a condição da parceria
                {cheio ? <>; fora dela, a mensalidade volta para {cheio}</> : null}, e dá para cancelar antes da
                primeira cobrança sem pagar nada.
              </>
            ) : (
              <>
                Por ser cliente {oferta.partnerName}, você assina a HTS por {preco}/mês enquanto mantiver a condição da
                parceria{cheio ? <>; fora dela, a mensalidade volta para {cheio}</> : null}.
              </>
            )}{" "}
            Gráficos, book de ofertas, Estrutura DI, notícias, calendário e referências globais em uma experiência integrada.
          </p>
          <a href="#form" className="hto-btn hto-pri" data-cursor-hover>
            {meta ? <>Entrar sem pagar hoje</> : teste > 0 ? <>Começar meu teste de {teste} dias</> : <>Assinar por {preco}/mês</>} <ArrowUpRight size={16} />
          </a>
        </Reveal>
      </section>

      <div className="hto-stats">
        <div className="hto-c hto-sg hto-sg-3">
          {numeros.map(([v, l]) => <div key={l} className="hto-stat"><strong>{v}</strong><span>{l}</span></div>)}
        </div>
      </div>

      <SecoesDaPlataforma destino="#form" />

      <section className="hto-sec hto-escuro" id="form">
        <div className="hto-c hto-form">
          <div className="hto-head hto-center">
            <Rotulo>Assinatura · {oferta.partnerName}</Rotulo>
            <h2 className="hto-title">{meta ? "Entre hoje sem pagar." : teste > 0 ? `Comece seus ${teste} dias agora.` : `Assine por ${preco}/mês.`}</h2>
            <p className="hto-sub">
              Dúvida antes de assinar? <a href="mailto:hts@hightrading.com.br">hts@hightrading.com.br</a>
            </p>
          </div>
          <CheckoutForm remaining={null} oferta={oferta} />
        </div>
      </section>

      <Faq />
      {/* A condição inteira vai DENTRO do rodapé, recolhível (dono, 18/10): é o texto que o aceite do checkout grava; o
          asterisco do título e dos números aponta para cá. */}
      {/* O "condições da oferta" do checkbox do checkout leva a este bloco (20/10). */}
      <Footer {...RODAPE} tituloDoDisclaimer="Leia as regras da condição" idDoDisclaimer={ID_DAS_CONDICOES} disclaimer={meta ? (oferta.condicaoBlocos?.length
        ? <CondicaoEmBlocos blocos={oferta.condicaoBlocos} marca="*" cor="#a1a1aa" />
        : <p style={{ maxWidth: 820, margin: "0 auto", fontSize: 12.5, lineHeight: 1.6, color: "#a1a1aa" }}>* {oferta.condicao}</p>) : undefined} />
    </div>
  );
}
